import { BadgeColors, Color, isInDesktopApp } from '@ftr/foundation'
import { HelpSubMenuItem } from '../help-sub-menu-flyout/item/help-sub-menu-flyout-item.component'

export const HelpCenterItem: HelpSubMenuItem = {
  title: 'Help Center',
  description: 'Access knowledge base articles for FTR Justice Cloud',
  icon: 'HelpCircleSolid',
  iconColor: BadgeColors.Grape,
  badgeColor: Color.Grape10,
  visible: true,
  isElevio: true,
}

export const HelpCenterPublicItem: HelpSubMenuItem = {
  ...HelpCenterItem,
  elevioClass: 'elevio__help-public',
}

export const SUPPORT_PORTAL_LINK = 'https://ftrgroup.service-now.com/customer'

export const SupportPortalItem: HelpSubMenuItem = {
  title: 'Support Portal',
  description: 'Submit or track a support request, or search the FTR Gold knowledge base',
  icon: 'AuditLog',
  iconColor: BadgeColors.Primary,
  badgeColor: Color.Primary10,
  link: SUPPORT_PORTAL_LINK,
  visible: true,
}

export const ProductUpdatesItem: HelpSubMenuItem = {
  title: 'Product Updates',
  description: 'The latest release notes for FTR Justice Cloud and patches for FTR Gold',
  icon: 'ErrorCircleSolid',
  iconColor: BadgeColors.Mint,
  badgeColor: Color.Mint10,
  link: 'https://fortherecord.releasenotes.io',
  visible: true,
}

export const SystemStatusItem: HelpSubMenuItem = {
  title: 'System Status',
  description: 'Check the status of FTR Justice Cloud and subscribe to incident updates',
  icon: 'CheckmarkCircle',
  iconColor: BadgeColors.Apple,
  badgeColor: Color.Apple10,
  link: 'https://fortherecord.statuspage.io',
  visible: true,
}

export const TrustCenterItem: HelpSubMenuItem = {
  title: 'Trust Center',
  description: "Public access to FTR Justice Cloud's compliance and security posture",
  icon: 'Verify',
  iconColor: BadgeColors.Lemon,
  badgeColor: Color.Lemon10,
  link: 'https://trust.fortherecord.com',
  visible: true,
}

export const CommunityForumItem: HelpSubMenuItem = {
  title: 'Community Forum',
  description: 'The home for discussion about all For The Record products',
  icon: 'Forum',
  iconColor: BadgeColors.Watermelon,
  badgeColor: Color.Watermelon10,
  link: 'https://fortherecord.discourse.group/signup',
  visible: true,
}

export function desktopAppsItem(
  desktopAppDownloadUrl: string,
  { isFtrForumEnabled, isLegacyApp }: { isFtrForumEnabled: boolean; isLegacyApp: boolean },
): HelpSubMenuItem {
  return {
    title: isLegacyApp ? 'FTR Justice Cloud Apps' : 'Desktop Apps',
    description: 'Download the latest FTR Justice Cloud apps for performance and connectivity',
    icon: 'Remote',
    iconColor: isFtrForumEnabled ? BadgeColors.Mandarin : BadgeColors.Watermelon,
    badgeColor: isFtrForumEnabled ? Color.Mandarin10 : Color.Watermelon10,
    link: desktopAppDownloadUrl,
    visible: true,
  }
}

export function publicPortalItem(publicPortalUrl: string): HelpSubMenuItem {
  return {
    title: 'Public Portal',
    description: 'Request transcripts or recordings.',
    icon: 'PeopleLine',
    iconColor: BadgeColors.Blueberry,
    badgeColor: Color.Blueberry10,
    link: publicPortalUrl,
    visible: true,
  }
}

export function courtPortalItem(courtsAppUrl: string): HelpSubMenuItem {
  return {
    title: 'Court Portal',
    description: 'View and manage court content. For authorized court staff only.',
    icon: 'Courthouse',
    iconColor: BadgeColors.Mint,
    badgeColor: Color.Mint10,
    link: courtsAppUrl,
    visible: true,
  }
}

export function getCourtsHelpSupport(params: {
  desktopAppDownloadUrl: string
  isFtrForumEnabled: boolean
  hasCourtSystems: boolean
  publicPortalUrl?: string
  isLegacyApp: boolean
}): HelpSubMenuItem[] {
  const items: HelpSubMenuItem[] = [
    HelpCenterItem,
    SupportPortalItem,
    ProductUpdatesItem,
    SystemStatusItem,
    TrustCenterItem,
  ]
  if (!isInDesktopApp()) {
    items.push(
      desktopAppsItem(params.desktopAppDownloadUrl, {
        isFtrForumEnabled: params.isFtrForumEnabled,
        isLegacyApp: params.isLegacyApp,
      }),
    )
  }
  if (params.isFtrForumEnabled && params.hasCourtSystems) {
    items.push(CommunityForumItem)
  }
  if (params.publicPortalUrl) {
    items.push(publicPortalItem(params.publicPortalUrl))
  }
  return items
}

export function getPublicPortalHelpSupport(params: {
  desktopAppDownloadUrl: string
  isFtrForumEnabled: boolean
  courtsAppUrl: string
  isLoggedIn: boolean
  isLegacyApp: boolean
}): HelpSubMenuItem[] {
  const items: HelpSubMenuItem[] = params.isLoggedIn
    ? [HelpCenterPublicItem, SupportPortalItem, ProductUpdatesItem, SystemStatusItem, TrustCenterItem]
    : [HelpCenterPublicItem, SupportPortalItem, SystemStatusItem]
  if (!isInDesktopApp()) {
    items.push(
      desktopAppsItem(params.desktopAppDownloadUrl, {
        isFtrForumEnabled: params.isFtrForumEnabled,
        isLegacyApp: params.isLegacyApp,
      }),
    )
  }

  if (params.isFtrForumEnabled && params.isLoggedIn) {
    items.push(CommunityForumItem)
  }

  if (params.isLoggedIn) {
    items.push(courtPortalItem(params.courtsAppUrl))
  }
  return items
}

export function getLoggedOutTopNavHelpSupport(params: { desktopAppDownloadUrl: string }): HelpSubMenuItem[] {
  const items: HelpSubMenuItem[] = [HelpCenterPublicItem, SupportPortalItem, SystemStatusItem]
  if (!isInDesktopApp()) {
    items.push(desktopAppsItem(params.desktopAppDownloadUrl, { isFtrForumEnabled: false, isLegacyApp: true }))
  }
  return items
}
